import { useEffect, useState } from "react";
import Modal from "../base/Modal";
import Icon from "../base/Icon";
import { openBrowserPhone, usePhone } from "../../core/Global";
import { toastError } from "../base/Toast";

/**
 * Returns true when the browser phone is connected and an extension is
 * registered (i.e. the user is able to place outgoing calls).
 * Reusable wherever you want to conditionally show click-to-call UI.
 */
export function usePhoneReady(): boolean {
    const phone = usePhone();
    const [ready, readySet] = useState<boolean>(false);

    useEffect(() => {
        const unsub = phone.subscribe(state => {
            const ok = (state.lines ?? []).some(
                l => l.registrationStatus === "registered" && l.transportConnected
            );
            readySet(ok);
        });
        return unsub;
    }, [phone]);

    return ready;
}

interface IProps {
    /** The phone number to dial. When undefined the modal is not rendered. */
    phone?: string;
    /** Optional display name shown in the confirmation text. */
    name?: string;
    onClose: () => void;
}

/**
 * Reusable confirmation modal for placing a call.
 * Shows the target number/name, and on confirm dials it through the browser phone.
 *
 * Usage:
 *   {callNumber && <CallConfirmModal phone={callNumber} name={callName} onClose={() => callNumberSet(undefined)} />}
 */
export default function CallConfirmModal(props: IProps) {

    const phone = usePhone();

    if (!props.phone) return null;

    return (
        <Modal id="modal-call-confirm"
            title="Confirm Call"
            open visible staticBackdrop
            buttonText="Call"
            buttonColor="success"
            buttonIcon="phone"
            onClose={props.onClose}
            onConfirm={(callback: any) => {
                // Close the modal immediately and open the big phone, then dial in the background.
                if (callback) callback();
                openBrowserPhone();
                phone.dial(props.phone!).catch((e: any) => {
                    // A refusal (no minutes, empty wallet) has already told the user why.
                    if (e?.name === "CallRefused") return;
                    toastError("Could not start the call. Make sure your phone is connected.");
                });
            }}>
            <div className="text-center py-5">
                <div className="mb-4">
                    <Icon icon="phone-volume" size={36} className="text-success" />
                </div>
                <div className="fs-5">
                    Are you sure you want to call
                </div>
                {
                    props.name &&
                    <div className="fs-4 fw-bold mt-2">
                        {props.name}
                    </div>
                }
                <div className={"fw-bold mt-1 " + (props.name ? "fs-6 text-gray-700" : "fs-3")}>
                    {props.phone}
                </div>
            </div>
        </Modal>
    )
}
